/**
 * AppV2ViewModel.ts
 *
 * ViewModel for managing the initialization and authentication logic for the V2 app context.
 */
// src/viewmodel/AppV2ViewModel.ts
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setUser } from "../store/slice/userSlice";
import { clearAllRelevantCookies, setAllCookies } from "../utils/cookiesUtils";
const BASE_URL = import.meta.env.VITE_HUSK_NEURON_URL;



/**
 * useAppV2ViewModel
 * Handles V2 app initialization, authentication, and error handling.
 */
export const useAppV2ViewModel = () => {
  const [appReady, setAppReady] = useState(false);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [delayedReady, setDelayedReady] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!window.opener) {
      setError("This page must be opened from Neuron. Redirecting...");
      setTimeout(() => {
        window.location.href = BASE_URL;
      }, 2000);
      return;
    }

    window.opener.postMessage("child-ready", BASE_URL);

    const handler = (event: MessageEvent) => {
      if (event.origin !== BASE_URL) return;

      if (event.data?.type === "init" && event.data.data) {
        clearAllRelevantCookies();
        setAllCookies(event.data.data);
        setCustomerId(event.data.data.customerId);

        dispatch(
          setUser({
            email: event.data.data.email || "",
            name: event.data.data.name || "",
            userData: event.data.data,
          })
        );

        setAppReady(true);
        window.opener?.postMessage("init-received", event.origin);
        setTimeout(() => setDelayedReady(true), 500);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [dispatch]);

  return { appReady, delayedReady, error };
};
